import {
  isDeclinableJobType,
  type DeclinableJobType,
  type JobType,
} from '../enums/job-type.enum.js'
import { formatMoneyValue, parseMoney, priceMinutes } from './money.js'
import { taxOnPortion } from './tax.js'
import { priceIrrigationSite } from './irrigation-pricing.js'
import {
  defaultWorkChoice,
  type TreeWorkChoice,
} from '../enums/tree-work-choice.enum.js'
import type {
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  ProposalDocumentDto,
  TreeScheduleBlock,
  TreeScheduleRow,
} from '../dto/proposal-document.dto.js'
import type { ProposalSelectionDto } from '../dto/proposal-selection.dto.js'
import { groupByAreaName } from './areas.js'

/**
 * What a client has chosen not to pay for.
 *
 * Everything is identified by VALUE — a species name, a problem name, a site
 * name — never by id, for the same reason the document snapshots them that
 * way. The selection is made against a frozen document, and an id would let a
 * rename in the app change what somebody had agreed to.
 *
 * Empty means "all of it", which is the state every proposal opens in.
 */
export interface ProposalSelection {
  /** Property names the client does not want work on. */
  excludedSites: string[]
  /** Species names, e.g. "Cedar elm". */
  excludedSpecies: string[]
  /**
   * Kinds of work. OTHER is never here — access, setup and clearing up have
   * to happen for the crew to be on the property at all.
   */
  excludedJobTypes: DeclinableJobType[]
  /** Problem names, e.g. "Mistletoe". */
  excludedProblems: string[]
  /**
   * Per-tree choices, stored ONLY where they differ from the default.
   *
   * Keeping deviations rather than every tree means an empty selection prices
   * exactly what the proposal offered — which matters because this same
   * arithmetic runs in a browser as somebody toggles and again on the server
   * when they accept.
   *
   * Identified by property and label because that is what a client sees and
   * what the document froze. Labels repeat across properties, so neither
   * alone would do.
   */
  treeChoices: TreeChoice[]
  /**
   * Trees the client turned off one by one.
   *
   * Identified by property and label, the same way a prune-or-remove choice
   * is. This list is ONLY the boxes somebody unticked. Turning off a species,
   * a site, or an area does not write into it, and turning that group back on
   * does not clear it — a tree left out by hand stays left out.
   */
  excludedTrees: ExcludedTree[]
  /**
   * Individual areas left out, by the id frozen on the document.
   *
   * A name would not do: two rings on one site may both be called "Common
   * Area", and the client can turn one of them off. The id is the snapshot's,
   * so an area deleted after send cannot change what was agreed.
   */
  excludedAreas: ExcludedArea[]
  /**
   * Every ring on that site which currently shares this name.
   *
   * Site-scoped on purpose. "Common Area" on the entrance drive is not the
   * courtyard behind the pool.
   *
   * Both area fields cover irrigation too: a repair is in its timer's area,
   * and a client leaving an area out leaves out everything in it.
   */
  excludedAreaNames: ExcludedAreaName[]
  /**
   * Every irrigation repair declined.
   *
   * A client can turn down the irrigation and keep the trees, or a kind of
   * repair, a zone or an area of it (see "On the proposal" in irrigation.md),
   * but never the parts or labor inside one repair, for the reason they
   * cannot decline a tree's solution.
   */
  excludedIrrigation: boolean
  /**
   * Kinds of irrigation repair declined, by name ("Bad nozzle"). Kept apart
   * from tree problems, whose names can coincide ("Other").
   */
  excludedIrrigationKinds: string[]
  /** Zones declined, by the survey's name for them at a property. */
  excludedZones: ExcludedZone[]
  /**
   * Single repairs left out, by the key frozen on the document. How a plan
   * scopes irrigation, as `excludedTrees` is how it scopes trees.
   */
  excludedRepairs: ExcludedRepair[]
}

/** A zone declined at one property: "Clubhouse 4" at Kings Lake. */
export interface ExcludedZone {
  site: string
  zone: string
}

/** One repair left out, addressed by its key on the document. */
export interface ExcludedRepair {
  site: string
  key: string
}

/** One tree switched off on its own. */
export interface ExcludedTree {
  site: string
  label: string
}

/** One ring switched off, addressed by the id on the document. */
export interface ExcludedArea {
  site: string
  areaId: string
}

/** Every ring of this name on this site. */
export interface ExcludedAreaName {
  site: string
  name: string
}

/** One tree the client has decided something different about. */
export interface TreeChoice {
  site: string
  label: string
  choice: TreeWorkChoice
}

export const EMPTY_SELECTION: ProposalSelection = {
  excludedSites: [],
  excludedSpecies: [],
  excludedJobTypes: [],
  excludedProblems: [],
  treeChoices: [],
  excludedTrees: [],
  excludedAreas: [],
  excludedAreaNames: [],
  excludedIrrigation: false,
  excludedIrrigationKinds: [],
  excludedZones: [],
  excludedRepairs: [],
}

/** What one pricing row comes to under a selection. */
export interface SiteOutcome {
  /**
   * The label on the row. The site name, or "Site — Area" when the proposal
   * is itemized by area.
   */
  name: string
  /** The property this row belongs to. */
  siteName: string
  /**
   * The area name this row bills.
   *
   * Null when the row is the whole site, and null on a remainder row. Those
   * two never exist for the same site at once.
   */
  areaName: string | null
  /** Exact decimal string, or null when nothing here can be priced. */
  amount: string | null
  /** True when the whole property has been declined. */
  isExcluded: boolean
}

/**
 * Whether a priced row is this outcome.
 *
 * A document frozen before areas omitted both fields. Its site name is the
 * row label, and a missing area is the whole site.
 */
export function pricingRowMatches(
  outcome: SiteOutcome,
  entry: {
    name: string
    siteName?: string | null | undefined
    areaName?: string | null | undefined
  },
): boolean {
  return (
    outcome.siteName === (entry.siteName ?? entry.name) &&
    outcome.areaName === (entry.areaName ?? null)
  )
}

/**
 * What one property comes to, adding its area rows back together.
 *
 * A tree quoted both ways is priced by running the whole proposal, and an
 * itemized site is then several rows. The figure for the tree is their sum.
 * A null amount is a row that could not be priced; if every row is null,
 * the site is too.
 */
export function amountForSite(
  sites: readonly SiteOutcome[],
  siteName: string,
): string | null {
  let cents: number | null = null
  for (const entry of sites) {
    if (entry.siteName !== siteName) continue
    const parsed = parseMoney(entry.amount)
    if (parsed === null) continue
    cents = (cents ?? 0) + parsed
  }
  return cents === null ? null : formatMoneyValue(cents)
}

export interface SelectionOutcome {
  sites: SiteOutcome[]
  /** The price of the selected work, before tax. */
  totalAmount: string | null
  /**
   * Tax on that price. Null when this proposal charges none.
   *
   * Computed from the rate frozen on the document, not from the settings as
   * they stand now. A rate changed after send governs the next proposal.
   */
  taxAmount: string | null
  /**
   * What the client pays: the selected work plus tax.
   *
   * Equals `totalAmount` when no tax is charged, and null when the work
   * itself could not be priced.
   */
  amountDue: string | null
  /**
   * What the client has declined, in money, before tax.
   *
   * The price of the work left out, not the tax that will not be charged on
   * it. Null when none of it priced.
   */
  deferredAmount: string | null
  /** How many trees are affected by at least one exclusion. */
  affectedTreeCount: number
  /**
   * Each site's irrigation repairs under the selection, one row per site
   * with repairs. Empty on a proposal that carries no irrigation.
   */
  irrigationSites: IrrigationSiteOutcome[]
}

/** What one site's irrigation repairs come to under a selection. */
export interface IrrigationSiteOutcome {
  siteName: string
  /** Before tax, including the drive charge while any work is left. */
  amount: string
  /** The part of `amount` Housecall Pro marks taxable. */
  taxable: string
  /** The drive charge added, or null when none applies. */
  driveCharge: string | null
  isExcluded: boolean
}

/** The species label used when a tree was never identified. */
export const UNIDENTIFIED_SELECTION_KEY = '__unidentified__'

/** A tree's species as the selection addresses it. */
export function speciesKey(species: { name: string } | null): string {
  return species?.name ?? UNIDENTIFIED_SELECTION_KEY
}

/** How many trees a job-type toggle actually reaches, and how many carry it. */
export function jobTypeReach(
  document: ProposalDocumentDto,
  type: JobType,
): { declinable: number; total: number } {
  // Work that cannot be declined reaches nothing, whatever it was itemized at.
  const canDecline = isDeclinableJobType(type)
  let declinable = 0
  let total = 0
  for (const block of scheduleBlocks(document)) {
    for (const row of block.rows) {
      const job = row.jobs.find((candidate) => candidate.type === type)
      if (job === undefined) continue
      total += 1
      if (canDecline && job.minutes !== null) declinable += 1
    }
  }
  return { declinable, total }
}

/** Area names on a site, in the order the rings were frozen. */
function areaNamesForSite(
  document: ProposalDocumentDto,
  siteName: string,
): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  for (const block of document.blocks) {
    if (block.type !== 'siteMap' || block.siteName !== siteName) continue
    for (const area of block.areas ?? []) {
      if (seen.has(area.name)) continue
      seen.add(area.name)
      names.push(area.name)
    }
  }
  return names
}

function scheduleBlocks(document: ProposalDocumentDto): TreeScheduleBlock[] {
  const blocks: TreeScheduleBlock[] = []
  for (const block of document.blocks) {
    if (block.type === 'treeSchedule') blocks.push(block)
  }
  return blocks
}

/**
 * The selection an accepted version was accepted at, as the lib prices it.
 *
 * A client accepting through their link stores what they chose. Staff
 * recording an acceptance store nothing, which means the proposal as it was
 * offered. Stored job types are narrowed to the declinable ones, as the input
 * that wrote them already did.
 */
export function acceptedSelection(
  stored: ProposalSelectionDto | null,
): ProposalSelection {
  if (stored === null) return EMPTY_SELECTION
  return {
    excludedSites: stored.excludedSites,
    excludedSpecies: stored.excludedSpecies,
    excludedJobTypes: stored.excludedJobTypes.filter(isDeclinableJobType),
    excludedProblems: stored.excludedProblems,
    treeChoices: stored.treeChoices,
    excludedTrees: stored.excludedTrees,
    excludedAreas: stored.excludedAreas,
    excludedAreaNames: stored.excludedAreaNames,
    excludedIrrigation: stored.excludedIrrigation,
    excludedIrrigationKinds: stored.excludedIrrigationKinds,
    excludedZones: stored.excludedZones,
    excludedRepairs: stored.excludedRepairs,
  }
}

/** Every site's irrigation repairs, as frozen into the document. */
export function irrigationScheduleBlocks(
  document: ProposalDocumentDto,
): IrrigationScheduleBlock[] {
  const blocks: IrrigationScheduleBlock[] = []
  for (const block of document.blocks) {
    if (block.type === 'irrigationSchedule') blocks.push(block)
  }
  return blocks
}

/** The names of a site's areas by their frozen id, for an area declined by id. */
function areaNamesById(
  document: ProposalDocumentDto,
  siteName: string,
): Map<string, string> {
  const names = new Map<string, string>()
  for (const block of document.blocks) {
    if (block.type !== 'siteMap' || block.siteName !== siteName) continue
    for (const area of block.areas ?? []) names.set(area.id, area.name)
  }
  return names
}

/**
 * Whether a selection leaves a repair out, for any reason.
 *
 * Exclusions are a union, as they are for trees: the property, all
 * irrigation, the repair's kind, its zone, its area, or the repair itself.
 * Turning one of them back on does not bring back what another still leaves
 * out.
 */
export function repairDropped(
  document: ProposalDocumentDto,
  siteName: string,
  repair: IrrigationRepairRow,
  selection: ProposalSelection,
): boolean {
  if (selection.excludedSites.includes(siteName)) return true
  if (selection.excludedIrrigation) return true
  if (selection.excludedIrrigationKinds.includes(repair.kind)) return true
  if (
    repair.zone !== null &&
    selection.excludedZones.some(
      (zone) => zone.site === siteName && zone.zone === repair.zone,
    )
  ) {
    return true
  }
  if (
    selection.excludedRepairs.some(
      (excluded) => excluded.site === siteName && excluded.key === repair.key,
    )
  ) {
    return true
  }
  if (repair.areaName === null) return false
  if (
    selection.excludedAreaNames.some(
      (area) => area.site === siteName && area.name === repair.areaName,
    )
  ) {
    return true
  }
  const byId = selection.excludedAreas.filter((area) => area.site === siteName)
  if (byId.length === 0) return false
  const names = areaNamesById(document, siteName)
  return byId.some((area) => names.get(area.areaId) === repair.areaName)
}

/**
 * The irrigation repairs at each site under a selection, priced by the
 * site's frozen rate card (see "Pricing" in irrigation.md). Whatever the
 * selection leaves out is gone, and the drive charge with the last of it.
 */
function priceIrrigation(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): {
  sites: IrrigationSiteOutcome[]
  cents: number
  taxableCents: number
  fullCents: number
} {
  const sites: IrrigationSiteOutcome[] = []
  let cents = 0
  let taxableCents = 0
  let fullCents = 0
  for (const block of irrigationScheduleBlocks(document)) {
    const isExcluded = selection.excludedSites.includes(block.siteName)
    fullCents +=
      parseMoney(priceIrrigationSite(block.repairs, block.rateCard).amount) ?? 0
    const kept = block.repairs.filter(
      (repair) => !repairDropped(document, block.siteName, repair, selection),
    )
    const priced = priceIrrigationSite(kept, block.rateCard)
    cents += parseMoney(priced.amount) ?? 0
    taxableCents += parseMoney(priced.taxable) ?? 0
    sites.push({
      siteName: block.siteName,
      amount: priced.amount,
      taxable: priced.taxable,
      driveCharge: priced.driveCharge,
      isExcluded,
    })
  }
  return { sites, cents, taxableCents, fullCents }
}

/**
 * The part of a whole-document total that is taxed: every tree dollar, and the
 * irrigation items Housecall Pro marks taxable. With no irrigation it is the
 * total itself, which is how every proposal was taxed before.
 */
export function taxableAmountOf(
  document: ProposalDocumentDto,
  totalAmount: string | null,
): string | null {
  const total = parseMoney(totalAmount)
  if (total === null) return null
  const irrigation = priceIrrigation(document, EMPTY_SELECTION)
  return formatMoneyValue(total - (irrigation.cents - irrigation.taxableCents))
}

function ratePerHourCents(document: ProposalDocumentDto): number | null {
  for (const block of document.blocks) {
    if (block.type === 'pricing') return parseMoney(block.ratePerHour)
  }
  return null
}

/**
 * The tax rate frozen onto this proposal, as a percent string.
 *
 * Null when the document charges none, including one frozen before tax
 * existed. Read from the document rather than from settings: the page and
 * the acceptance API both have to apply the rate the client was offered.
 */
export function documentTaxRate(document: ProposalDocumentDto): string | null {
  for (const block of document.blocks) {
    if (block.type === 'pricing') return block.taxRatePercent
  }
  return null
}

/** Whether this tree is quoted for pruning at all. */
function hasPruning(row: TreeScheduleRow): boolean {
  return (
    row.jobs.length > 0 ||
    row.problems.length > 0 ||
    (row.estimatedLaborMinutes ?? 0) > 0 ||
    (row.pruningPriceOverride ?? null) !== null
  )
}

/**
 * What the client has settled on for one tree.
 *
 * An explicit choice where they made one, the default otherwise. The default
 * is pruning where it is offered — the conservative answer, which a client
 * opts UP from rather than being defaulted into a removal — except on a tree
 * quoted only for removal, where leaving it out would open the proposal
 * understating its own total.
 */
export function treeChoiceFor(
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): TreeWorkChoice {
  const explicit = selection.treeChoices.find(
    (entry) => entry.site === siteName && entry.label === row.label,
  )
  if (explicit !== undefined) return explicit.choice
  return defaultWorkChoice({
    hasPruning: hasPruning(row),
    hasRemoval: row.removal !== null,
  })
}

/**
 * Why a broader filter has already taken this tree off the bid.
 *
 * The tree's own box is not one of these. Unticking a species mutes those
 * trees and names the species; it does not untick them. The site comes before
 * the area, and the area before the species, because that is the order a
 * person would undo them.
 */
export type SelectionDropReason = 'site' | 'area' | 'areaName' | 'species'

/** The broadest reason this tree is already off, or null when only its box is. */
export function selectionDropReason(
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): SelectionDropReason | null {
  if (selection.excludedSites.includes(siteName)) return 'site'
  const areaId = row.areaId ?? null
  if (
    areaId !== null &&
    selection.excludedAreas.some(
      (entry) => entry.site === siteName && entry.areaId === areaId,
    )
  ) {
    return 'area'
  }
  const areaName = row.areaName ?? null
  if (
    areaName !== null &&
    areaName !== '' &&
    selection.excludedAreaNames.some(
      (entry) => entry.site === siteName && entry.name === areaName,
    )
  ) {
    return 'areaName'
  }
  if (selection.excludedSpecies.includes(speciesKey(row.species))) {
    return 'species'
  }
  return null
}

/**
 * Whether this tree's whole price comes out.
 *
 * A site, an area, an area name, a species, or the tree's own box. A
 * prune-or-remove choice is not in here: removal still has a price. Declining
 * a property also zeros the row in `priceSelection` before this runs, so the
 * site is not counted twice.
 */
export function selectionDropsTree(
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): boolean {
  if (selectionDropReason(siteName, row, selection) !== null) return true
  return selection.excludedTrees.some(
    (entry) => entry.site === siteName && entry.label === row.label,
  )
}

/**
 * What the pruning kept on one tree costs, in cents.
 *
 * The crew rate on the kept minutes, unless the arborist set the price by
 * hand. Then a declined kind of work takes its share of that price by its
 * minutes, which is the share it would have taken at the crew rate: a client
 * declining a third of the work gets a third off, whoever set the figure. A
 * tree with no minutes has no shares to take, so its price stands whole.
 */
function pruningCents(
  row: TreeScheduleRow,
  keptMinutes: number,
  allMinutes: number,
  centsPerHour: number | null,
): number {
  const override = parseMoney(row.pruningPriceOverride ?? null)
  if (override !== null) {
    if (allMinutes === 0 || keptMinutes === allMinutes) return override
    return Math.round((override * keptMinutes) / allMinutes)
  }
  return centsPerHour === null ? 0 : priceMinutes(keptMinutes, centsPerHour)
}

/**
 * What one tree costs under a selection, in cents.
 *
 * Money only — the client is not shown hours, so there is no duration to
 * report back. Two pools add up: the crew time for the tree's jobs, and the
 * treatments for its problems. A problem the client declined takes its
 * treatments with it; a kind of work they declined takes its minutes.
 */
function priceTree(
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
  centsPerHour: number | null,
): number {
  if (selectionDropsTree(siteName, row, selection)) return 0

  const choice = treeChoiceFor(siteName, row, selection)
  if (choice === 'NONE') return 0

  /*
   * Removal REPLACES pruning, and takes the tree's problem treatments with
   * it. You do not treat a tree you are taking down, and charging for both
   * would be quoting work nobody would do.
   */
  if (choice === 'REMOVE') {
    return parseMoney(row.removal?.amount ?? null) ?? 0
  }

  const itemized =
    row.jobs.length > 0 && row.jobs.every((job) => job.minutes !== null)

  /*
   * An un-itemized tree keeps its whole estimate whatever is toggled: there
   * is no per-job figure to subtract, so declining a kind of work cannot
   * reduce it. That is the trade for itemizing being optional.
   */
  const minutes = itemized
    ? row.jobs
        .filter(
          (job) =>
            !isDeclinableJobType(job.type) ||
            !selection.excludedJobTypes.includes(job.type),
        )
        .reduce((sum, job) => sum + (job.minutes ?? 0), 0)
    : (row.estimatedLaborMinutes ?? 0)
  const allMinutes = itemized
    ? row.jobs.reduce((sum, job) => sum + (job.minutes ?? 0), 0)
    : (row.estimatedLaborMinutes ?? 0)

  const jobCents = pruningCents(row, minutes, allMinutes, centsPerHour)

  const problemCents = row.problems
    .filter((problem) => !selection.excludedProblems.includes(problem.name))
    .reduce((sum, problem) => sum + (parseMoney(problem.amount) ?? 0), 0)

  return jobCents + problemCents
}

/**
 * What one tree costs under a selection, in cents, before tax.
 *
 * The same arithmetic `priceSelection` uses, exposed for the plans: a budget
 * fit has to add trees up one at a time, and a second copy of this would be a
 * second answer to what a tree costs.
 */
export function treeAmountCents(
  document: ProposalDocumentDto,
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection = EMPTY_SELECTION,
): number {
  if (selection.excludedSites.includes(siteName)) return 0
  return priceTree(siteName, row, selection, ratePerHourCents(document))
}

/** Whether anything about this tree has been declined. */
function isAffected(
  siteName: string,
  row: TreeScheduleRow,
  selection: ProposalSelection,
): boolean {
  if (selectionDropsTree(siteName, row, selection)) return true
  if (
    treeChoiceFor(siteName, row, selection) !==
    treeChoiceFor(siteName, row, EMPTY_SELECTION)
  ) {
    return true
  }
  if (
    row.problems.some((problem) =>
      selection.excludedProblems.includes(problem.name),
    )
  ) {
    return true
  }
  return row.jobs.some(
    (job) =>
      job.minutes !== null &&
      isDeclinableJobType(job.type) &&
      selection.excludedJobTypes.includes(job.type),
  )
}

/**
 * Price a whole proposal under a selection.
 *
 * Shared rather than implemented on each side: the client watches this number
 * change as they toggle, and the API recomputes it when they accept — because
 * what arrives from a browser is a set of choices, never a price. Two
 * implementations would be two answers to what was agreed, and the one on the
 * invoice would be whichever ran last.
 */
export function priceSelection(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): SelectionOutcome {
  const centsPerHour = ratePerHourCents(document)
  const taxRatePercent = documentTaxRate(document)
  const blocks = scheduleBlocks(document)

  let total = 0
  let fullTotal = 0
  let affectedTreeCount = 0
  const sites: SiteOutcome[] = []

  for (const block of blocks) {
    const isExcluded = selection.excludedSites.includes(block.siteName)
    const areaNames = areaNamesForSite(document, block.siteName)
    const groups = groupByAreaName(
      block.siteName,
      block.rows,
      areaNames,
      document.itemizeByArea,
    )

    for (const group of groups) {
      let groupCents = 0
      for (const row of group.items) {
        fullTotal += priceTree(
          block.siteName,
          row,
          EMPTY_SELECTION,
          centsPerHour,
        )
        if (isExcluded) {
          affectedTreeCount += 1
          continue
        }
        groupCents += priceTree(block.siteName, row, selection, centsPerHour)
        if (isAffected(block.siteName, row, selection)) affectedTreeCount += 1
      }

      total += isExcluded ? 0 : groupCents
      const amount =
        centsPerHour === null && groupCents === 0
          ? null
          : formatMoneyValue(isExcluded ? 0 : groupCents)
      sites.push({
        name: group.displayName,
        siteName: block.siteName,
        areaName: group.split ? group.areaName : null,
        amount,
        isExcluded,
      })
    }
  }

  const irrigation = priceIrrigation(document, selection)
  const deferred = fullTotal + irrigation.fullCents - (total + irrigation.cents)
  const hasWork = blocks.length > 0 || irrigation.sites.length > 0
  const totalAmount = hasWork
    ? formatMoneyValue(total + irrigation.cents)
    : null
  /*
   * Tax on the whole selected price, not on the pruning labor inside it.
   * `total` already adds each problem's treatments — a per-tree fee, cable
   * by the foot, diameter, time at a solution's own rate — to the crew time
   * or the removal that replaced it. Taxing the labor portion alone would
   * bill the board for the work and forget the materials.
   *
   * Irrigation is the exception, taxed item by item as Housecall Pro marks
   * it (residential labor is not taxed), so only its taxable part joins the
   * trees'. Still one rounding, on the combined taxable figure.
   */
  const taxed = taxOnPortion(
    totalAmount,
    formatMoneyValue(total + irrigation.taxableCents),
    taxRatePercent,
  )

  return {
    sites,
    totalAmount,
    taxAmount: taxed.taxAmount,
    amountDue: taxed.amountDue,
    deferredAmount: deferred <= 0 ? null : formatMoneyValue(deferred),
    affectedTreeCount,
    irrigationSites: irrigation.sites,
  }
}
